import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert, Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  AtsAddApplicationsGetCandidatesByEmailsDocument,
  AtsAddApplicationsGetEmployeesDocument,
  AtsCreateApplicationsFromEmployeesDocument,
  EmployeeForSelectFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { joinAsSentence } from 'modules/core/lib/stringUtils'
import app from 'modules/core/store/models/app'

import { useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { EntitySelectorWrapper } from 'modules/employees/components/EmployeeSelector/EntitySelectorWrapper'

const normalizeEmail = (email?: string | null): string => {
  if (!email) return ''
  return email.trim().toLowerCase()
}

const useExistingApplications = (employeeIds: number[], jobPostingId: number) => {
  const skip = !jobPostingId || employeeIds.length === 0

  const { data: employeesData, loading: employeesLoading } = useQuery(
    AtsAddApplicationsGetEmployeesDocument,
    {
      variables: { ids: employeeIds },
      fetchPolicy: 'cache-and-network',
      skip,
    }
  )

  const employeesEmails = useUnsafeMemo(() => {
    const employees = employeesData?.employees?.employeesConnection?.nodes ?? []

    if (employees.length === 0) return []

    const emailsArray: string[] = []

    employees.forEach((employee) => {
      if (employee && employee.email) {
        const normalizedEmail = normalizeEmail(employee.email)
        if (normalizedEmail) {
          emailsArray.push(normalizedEmail)
        }
      }
    })

    return emailsArray
  }, [employeesData])

  const { data: candidatesData, loading: candidatesLoading } = useQuery(
    AtsAddApplicationsGetCandidatesByEmailsDocument,
    {
      variables: { emails: employeesEmails },
      fetchPolicy: 'cache-and-network',
      skip: employeesEmails.length === 0,
    }
  )

  const existingApplications = useUnsafeMemo(() => {
    const candidates = candidatesData?.ats.candidatesConnection.nodes ?? []

    if (!candidates) return []

    return candidates.filter((candidate) =>
      candidate.jobPostings?.some((posting) => posting.id === jobPostingId)
    )
  }, [candidatesData, jobPostingId])

  return {
    loading: employeesLoading || candidatesLoading,
    existingApplications,
  }
}

const JobApplicationsAddEmployeeCandidatePage = observer(() => {
  const params = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([])
  const [selectedApplicationPhase, setSelectedApplicationPhase] = useState<
    string | null | undefined
  >()
  const jobId = Number(params.jobId)
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragment[]>([])

  const { loading: checkingExisting, existingApplications } = useExistingApplications(
    selectedEmployeeIds,
    jobId
  )

  const onClose = () => {
    navigate(navigation.ats.jobs.jobsId.applications.fullPath({ jobId }))
  }

  const { applicationPhases } = useApplicationPhases({
    variables: { atsJobPostingId: jobId },
  })

  const [createApplicationsFromEmployees, { loading: creating }] = useMutation(
    AtsCreateApplicationsFromEmployeesDocument,
    {
      extractorFn: (data) => data.atsMutations.createFromEmployeesAtsApplication,
    }
  )

  const phasesOptions = applicationPhases.map((phase) => ({
    label: phase.name,
    value: phase.id,
  }))

  const hasExistingApplications = existingApplications.length > 0

  const validEmployeeIds = useUnsafeMemo(() => {
    if (!hasExistingApplications) return selectedEmployeeIds

    const employees = selectedEmployees.filter((employee) => {
      return !existingApplications.some(
        (candidate) => candidate.fullName?.toLowerCase() === employee.fullName?.toLowerCase()
      )
    })

    return employees.map((employee) => employee.id)
  }, [selectedEmployeeIds, existingApplications, hasExistingApplications, selectedEmployees])

  const isFormCompleted = (): boolean => {
    return !!jobId && !!selectedApplicationPhase && validEmployeeIds.length > 0
  }

  const onSubmit = async () => {
    const result = await createApplicationsFromEmployees({
      variables: {
        employeeIds: validEmployeeIds,
        atsJobPostingId: jobId,
        atsApplicationPhaseId: Number(selectedApplicationPhase),
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }

    return handled
  }

  const onSelectEmployees = (employees: EmployeeForSelectFragment[]) => {
    setSelectedEmployees(employees)
    setSelectedEmployeeIds(employees.map((employee) => employee.id))
  }

  return (
    <Modal
      illustration="people-add"
      title={i18n.t('ats.candidates.employee_candidate_modal.title')}
      description={i18n.t('ats.candidates.employee_candidate_modal.application_description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!isFormCompleted() || creating || checkingExisting} submit>
            {i18n.t('ats.candidates.employee_candidate_modal.save')}
          </Button>
        </>
      }
    >
      {phasesOptions.length ? (
        <Box gap="s16" width="s400">
          <Select
            none={i18n.t('ats.candidates.add_to_another_job_posting.phase_placeholder')}
            label={i18n.t('ats.candidates.add_to_another_job_posting.phase')}
            options={phasesOptions}
            onChange={(value) => setSelectedApplicationPhase(value)}
          />

          <EntitySelectorWrapper
            multiple
            usingAccessIds
            label={i18n.t('common.employees')}
            onSelect={onSelectEmployees}
            value={selectedEmployees}
          />

          {hasExistingApplications && (
            <Alert color="warning">
              <Text.Normal>
                {i18n.tp('ats.candidates.employee_candidate_modal.existing_application_warning', {
                  count: existingApplications.length,
                  candidateNames: joinAsSentence(
                    existingApplications.map((candidate) => candidate.fullName).filter(Boolean)
                  ),
                })}
              </Text.Normal>
            </Alert>
          )}

          {checkingExisting && (
            <Box alignItems="center">
              <Spinner size="s16" />
            </Box>
          )}
        </Box>
      ) : (
        <Box gap="s16" alignItems="center">
          <Spinner />
        </Box>
      )}
    </Modal>
  )
})

export default JobApplicationsAddEmployeeCandidatePage
